import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { getBroadcastGroupDetails, getBroadcastGroups } from '@/services/api/broadcast';
import type { BroadcastGroupSummary } from '@/types/broadcast';
import type { BroadcastFormData } from '@/pages/Meta/Broadcast/types/broadcast.types';
import { meuIdSeAdmin } from './rotinaUtils';

const SUFIXO_DATA_HORA = /\s*\d{1,2}\/\d{1,2}\s*\((\d{1,2}):(\d{2})\)\s*$/;

/** Tira o "29/09 (7:15)" que o sistema acrescenta: o grupo guarda o título limpo. */
export function tituloLimpo(titulo: string): { titulo: string; horaLocal?: string } {
    const m = titulo.match(SUFIXO_DATA_HORA);
    if (!m) return { titulo };
    return { titulo: titulo.replace(SUFIXO_DATA_HORA, ''), horaLocal: `${m[1].padStart(2, '0')}:${m[2]}` };
}

type Origem = Pick<
    BroadcastGroupSummary,
    'title' | 'name' | 'template_params' | 'message_tag' | 'ai_translate' | 'source_language' | 'pages'
    | 'utility_template_id'
>;

export function formularioDeBroadcast(origem: Origem): BroadcastFormData {
    const limpo = tituloLimpo(origem.title || origem.name || '');
    const vistas = new Set<string>();
    const selectedPages = (origem.pages ?? [])
        .filter((p) => !vistas.has(p.page_id) && !!vistas.add(p.page_id))
        .map((p) => ({ id: p.id, page_id: p.page_id, page_name: p.page_name }));
    return {
        title: limpo.titulo || undefined,
        horaLocal: limpo.horaLocal,
        selectedPages,
        blocks: origem.template_params?.blocks ?? [],
        isScheduled: false,
        scheduledTime: '',
        // Duplicar tem de trazer o TIPO junto, não só o conteúdo: antes um
        // broadcast de utility virava grupo standard em silêncio, e o template
        // aprovado por página se perdia.
        broadcastType: origem.utility_template_id
            ? 'utility'
            : origem.message_tag ? 'message_tag' : 'standard',
        messageTag: origem.message_tag ?? null,
        utilityTemplateId: origem.utility_template_id ?? null,
        aiTranslate: !!origem.ai_translate,
        sourceLanguage: origem.source_language,
    };
}

export default function DuplicarBroadcastDialog({
    open,
    onOpenChange,
    onEscolher,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onEscolher: (formulario: BroadcastFormData) => void;
}) {
    const { toast } = useToast();
    const [busca, setBusca] = useState('');
    const [carregando, setCarregando] = useState<number | null>(null);

    const lista = useQuery({
        queryKey: ['rotina-duplicar-lista'],
        queryFn: () => getBroadcastGroups(1, 40, undefined, undefined, meuIdSeAdmin()),
        enabled: open,
    });

    const termo = busca.trim().toLowerCase();
    const grupos = (lista.data?.items ?? []).filter(
        (g) => !termo || (g.title || g.name || '').toLowerCase().includes(termo),
    );

    const escolher = async (g: BroadcastGroupSummary) => {
        setCarregando(g.group_broadcast_id);
        try {
            const detalhe = await getBroadcastGroupDetails(g.group_broadcast_id);
            onEscolher(formularioDeBroadcast({ ...g, ...detalhe }));
        } catch {
            toast({ variant: 'destructive', title: 'Não consegui carregar o broadcast', description: 'Tente de novo.' });
        } finally {
            setCarregando(null);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[85vh] max-w-xl overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Duplicar um broadcast</DialogTitle>
                    <DialogDescription>
                        Escolha um disparo que já funcionou. Conteúdo, páginas e tag vêm preenchidos no modal; a data some e
                        você escolhe só a hora.
                    </DialogDescription>
                </DialogHeader>
                <Input placeholder="Buscar pelo título" value={busca} onChange={(e) => setBusca(e.target.value)} />
                {lista.isLoading && <Loader2 className="h-5 w-5 animate-spin" />}
                {lista.isError && <p className="text-sm text-destructive">Não foi possível listar os broadcasts.</p>}
                <ul className="space-y-1">
                    {grupos.map((g) => {
                        const utility = !!g.utility_template_id;
                        return (
                            <li key={g.group_broadcast_id}>
                                <button
                                    type="button"
                                    disabled={utility || carregando !== null}
                                    onClick={() => escolher(g)}
                                    className="flex w-full items-center justify-between gap-3 rounded-md border p-2 text-left text-sm hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    <span className="min-w-0">
                                        <span className="block truncate">{g.title || g.name || `Grupo ${g.group_broadcast_id}`}</span>
                                        <span className="block text-xs text-muted-foreground">
                                            {g.total_pages} páginas
                                            {utility && ' · utility: a Rotina ainda não aceita'}
                                        </span>
                                    </span>
                                    {carregando === g.group_broadcast_id && <Loader2 className="h-4 w-4 shrink-0 animate-spin" />}
                                </button>
                            </li>
                        );
                    })}
                </ul>
                {!lista.isLoading && !lista.isError && grupos.length === 0 && (
                    <p className="text-sm text-muted-foreground">Nenhum broadcast encontrado entre os 40 mais recentes.</p>
                )}
            </DialogContent>
        </Dialog>
    );
}
